import React, { useState, useCallback } from 'react';
import { Brain } from 'lucide-react';
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebaseConfig";
import { useNavigate } from "react-router-dom";


// Lucide icon for email (reused)
const MailIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
);

// Input Field Component (reused for consistency)
const InputField = ({ label, type, value, onChange, placeholder, icon: Icon }) => (
  <div className="mb-6">
    <label className="block text-sm font-medium text-gray-700 sr-only" htmlFor={type}>
      {label}
    </label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
      <input
        id={type}
        type="email"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out shadow-sm text-sm"
        autoComplete="email"
      />
    </div>
  </div>
);
// --------------------------------------------------------------------


const App = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();

  const handleReset = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Firebase sendPasswordResetEmail call
      await sendPasswordResetEmail(auth, email);


      // This generic success message is standard practice for security
      setSuccess('If an account exists for ' + email + ', a password reset link has been sent to your inbox.');
      setEmail(''); // Clear the input field
    } catch (err) {
      console.error("Password Reset Error:", err);
      setError(err.message || 'An unexpected error occurred during the reset process.');
    } finally {
      setLoading(false);
    }
  }, [email]);

  // Handler for navigation (e.g., Back to Sign In)
  const handleBackToLogin = (e) => {
    e.preventDefault();
    navigate('/login');
  };

  return (
    // Load Tailwind CSS script
    <>

      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 font-sans">

        {/* Password Reset Card Container */}
        <div className="w-full max-w-md">
          <div className="bg-white p-8 sm:p-10 shadow-2xl rounded-3xl border border-gray-100/50 transform transition duration-500 hover:shadow-3xl">

            {/* Logo and Title Section (consistent with Login) */}
            <div className="flex flex-col items-center mb-6">
              {/* Logo */}
              <Brain className="text-blue-600 w-12 h-12 mb-2" />
              <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                Neuracraft Solutions
              </h1>
            </div>

            {/* Heading and Instructions */}
            <h2 className="text-xl font-semibold text-gray-800 text-center mb-2">
              Reset Your Password
            </h2>
            <p className="text-sm text-gray-500 text-center mb-8">
              Enter the email address associated with your account. We'll send you a link to reset your password.
            </p>

            <form onSubmit={handleReset}>
              {/* Email Input */}
              <InputField
                label="Email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Work email address"
                icon={MailIcon}
              />

              {/* Status Messages */}
              {error && (
                <div className="text-center p-3 my-4 bg-red-50 text-red-700 rounded-lg text-sm transition-all duration-300">
                  {error}
                </div>
              )}
              {success && (
                <div className="text-center p-3 my-4 bg-green-50 text-green-700 rounded-lg text-sm transition-all duration-300">
                  {success}
                </div>
              )}

              {/* Reset Button */}
              <button
                type="submit"
                disabled={loading || success} // Disable if loading or successful
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-md text-sm font-medium text-white transition duration-200 ease-in-out transform
                  ${loading || success
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 hover:scale-[1.01]'
                  }
                `}
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending Link...
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>

            {/* Back to Login Link */}
            <div className="mt-6 text-center">
              <a
                href="/login"
                onClick={handleBackToLogin}
                className="text-sm font-medium text-gray-500 hover:text-blue-600 hover:underline transition duration-150 flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
                Back to Sign In
              </a>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default App;
